import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

import { useChannelCreatorActions, useChannelCreatorMutations } from "@ChannelCreator/composables/useChannelCreator";
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { useGlobalParameters, useLicenses, useUserSettings } from "@common/composable/useRootStore";
import { useCatalogNodeMutations } from "@ContentEditor/store/composable/useCatalogNode";
import activityApi from "@services/activity";
import apps from "@services/applications";
import automationServerApi from "@services/automationServer";
import catalogApi from "@services/catalogs.js";
import { computed, reactive } from "vue";
import catalogFinishing from "./FromExternalSource/CatalogFinisherExternal.vue";
import catalogNaming from "./FromExternalSource/CatalogNamingExternal.vue";
import catalogOptions from "./FromExternalSource/CatalogOptionsExternal.vue";
import existingCatalog from "./FromExternalSource/ExistingCatalogExternal.vue";
import intro from "./FromExternalSource/IntroExternal.vue";

//#region store
type Step = "intro" | "existingCatalog" | "catalogOptions" | "catalogNaming" | "catalogFinishing";

type ViewModel = {
  currentStep: Step;
  connectionState: boolean | null;
  selectedExistingCatalogId: string | null;
  selectedExistingCatalogName: string | null;

  commandId: string | null;
  file: File | null;
  fileData: any | null;
  fields: { artNrTypeSelected: number; artNrSourceSelected: number | null } | null;

  selectedArticleNumberType: any | null;
  newCatalogName: string | null;
  catalogNameIsValid: boolean;
  finalData: any | null;
  generatedCatalogData: any | null;
  fetchedSourceFields: any | null;
  groupedCommands: any | null;
  singleColumnName: string | null;

  openGeneratedCatalogue: boolean;
  error: any | null;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'FromExternalSourceModal',
  props: {
    value: { type: Boolean }
  },
  emits: ["input"],
  setup(__props: any, { emit: emits }) {

const props = __props;

const { saveUserSettings } = useUserSettings();

const { loadCatalogs, updateTargetCatalog } = useChannelCreatorActions();
const { clearDirtyStatus: clearChannelCreatorDirtyStatus } = useChannelCreatorMutations();

const { "clear-dirty-status": clearCatalogNodeDirtyStatus } = useCatalogNodeMutations();





//#endregion

const { checkForLicensed } = useLicenses();
const { primaryLanguageCode } = useContentLanguages();
const { globalParameters } = useGlobalParameters();

const localModel = computed({
  get() {
    return props.value;
  },
  set(nv: boolean) {
    emits("input", nv);
  }
});

const getInitialViewModel = (): ViewModel => ({
  currentStep: "intro",
  connectionState: null,
  selectedExistingCatalogId: null,
  selectedExistingCatalogName: null,
  commandId: null,
  file: null,
  fileData: null,
  fields: null,
  selectedArticleNumberType: null,
  newCatalogName: null,
  catalogNameIsValid: false,
  finalData: null,
  generatedCatalogData: null,
  fetchedSourceFields: null,
  groupedCommands: null,
  singleColumnName: null,
  openGeneratedCatalogue: false,
  error: null
});

const vm = reactive<ViewModel>(getInitialViewModel());

const buttonConfig: Record<
  Step,
  {
    back?: { text: string; action: () => void; disabled: () => boolean };
    next: { text: string; action: () => void; disabled: () => boolean };
  }
> = {
  intro: {
    back: undefined,
    next: {
      text: "Continue",
      action: () => stepChange({ currentState: "intro" as Step, nextState: "existingCatalog" }),
      disabled: () => false
    }
  },
  existingCatalog: {
    back: {
      text: "Back",
      action: () => stepChange({ currentState: "existingCatalog", nextState: "intro" }),
      disabled: () => false
    },
    next: {
      text: "Continue",
      action: () => stepChange({ currentState: "existingCatalog", nextState: "catalogOptions" }),
      disabled: () => vm.selectedExistingCatalogId === null
    }
  },
  catalogOptions: {
    back: {
      text: "Back",
      action: () => stepChange({ currentState: "catalogOptions", nextState: "existingCatalog" }),
      disabled: () => false
    },
    next: {
      text: "Continue",
      action: () => stepChange({ currentState: "catalogOptions", nextState: "catalogNaming" }),
      disabled: () => vm.singleColumnName === null || vm.selectedArticleNumberType === null || vm.connectionState !== true
    }
  },
  catalogNaming: {
    back: {
      text: "Back",
      action: () => stepChange({ currentState: "catalogNaming", nextState: "catalogOptions" }),
      disabled: () => false
    },
    next: {
      text: "Continue",
      action: () => setCatalogName({ currentState: "catalogNaming", nextState: "catalogFinishing" }),
      disabled: () => !vm.newCatalogName || !vm.catalogNameIsValid
    }
  },
  catalogFinishing: {
    back: undefined,
    next: {
      text: "Close",
      action: () => onFinish(),
      disabled: () => false
    }
  }
};

const hasBackButton = computed(() => !!buttonConfig[vm.currentStep].back);
const backButtonDisabled = computed(() => buttonConfig[vm.currentStep].back?.disabled() ?? false);
const backButtonText = computed(() => buttonConfig[vm.currentStep].back?.text ?? "Back");

const hasNextButton = computed(() => !!buttonConfig[vm.currentStep].next);
const nextButtonDisabled = computed(() => buttonConfig[vm.currentStep].next.disabled());
const nextButtonText = computed(() => buttonConfig[vm.currentStep].next.text);

const onBack = () => buttonConfig[vm.currentStep].back?.action();
const onNext = () => buttonConfig[vm.currentStep].next.action();

function stepChange(e: { currentState: Step; nextState: Step }) {
  vm.currentStep = e.nextState;
  if (vm.currentStep === "intro") {
    reset();
  }
}

function onHide() {
  vm.currentStep = "intro";
  vm.selectedExistingCatalogId = null;
  vm.generatedCatalogData = null;
}

const hideDialog = () => {
  localModel.value = false;
};

function reset() {
  Object.assign(vm, getInitialViewModel());
}

function setExternalDataSelection(e: {
  singleColumnName: string | null;
  selectedArticleNumberType: any | null;
  connectionState: boolean | null;
  commandId: string | null;
}) {
  vm.singleColumnName = e.singleColumnName;
  vm.selectedArticleNumberType = e.selectedArticleNumberType;
  vm.connectionState = e.connectionState;
  vm.commandId = e.commandId;
}

async function setCatalogName(e: { currentState: Step; nextState: Step }) {
  stepChange(e);
  if (vm.currentStep === "catalogFinishing") {
    await generateCatalog();
  }
}

function onCatalogNameChange(e: { name: string; isValid: boolean }) {
  vm.newCatalogName = e.name;
  vm.catalogNameIsValid = e.isValid;
}

async function generateCatalog() {
  const data = {
    commandId: vm.commandId,
    selectedArticleNumberType: vm.selectedArticleNumberType,
    newCatalogName: vm.newCatalogName,
    selectedCatalogId: vm.selectedExistingCatalogId
  };

  try {
    vm.generatedCatalogData = await catalogApi.generateFromExternalData(primaryLanguageCode.value, data);
    await createActivity();

    if (checkForLicensed("AutomationServer")) {
      await executeAutomation();
    }
  } catch (exception: any) {
    vm.error = exception;
  }
}

async function createActivity() {
  try {
    if (globalParameters.value.IsActivityFeedEnabled) {
      const activityCriterias = [
        {
          severity: "Information",
          applicationId: apps.Modules.ChannelCreator.Id,
          process: "Create",
          objectType: "Catalog",
          objectValue: vm.newCatalogName || "",
          status: "Successful",
          action: "Completed",
          descriptionKey: "CatalogGeneratedFromCSV",
          descriptionValues: [vm.newCatalogName]
        }
      ];

      await activityApi.createActivity(activityCriterias);
    }
  } catch (err) {
    console.error("Activity creation failed:", err);
  }
}

async function executeAutomation() {
  try {
    await automationServerApi.executeAutomationsIfRequired(["CatalogCreated"]);
  } catch (err) {
    console.error("Automation execution failed:", err);
  }
}

function onFinish() {
  if (vm.openGeneratedCatalogue && vm.generatedCatalogData?.catalogId) {
    const catalogId = vm.generatedCatalogData.catalogId;
    try {
      loadCatalogs();
      //Timeout prevents JStree bug from loading it twice.
      setTimeout(() => {
        updateTargetCatalog(catalogId);
        saveUserSettings({ ChannelCreatorTargetCatalogId: catalogId });
        clearChannelCreatorDirtyStatus();
        clearCatalogNodeDirtyStatus();
      }, 100);
    } catch (err: unknown) {
      console.error("Reload catalogs failed:", err);
    }
  }
  hideDialog();
  vm.error = null;
}

return (_ctx: any,_cache: any) => {
  const _component_b_btn = _resolveComponent("b-btn")!
  const _component_b_modal = _resolveComponent("b-modal")!

  return (_openBlock(), _createBlock(_component_b_modal, {
    modelValue: localModel.value,
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((localModel).value = $event)),
    "header-close-content": "",
    id: "from-external-source-modal",
    "body-class": "modal-body",
    "modal-class": "from-external-source-modal",
    title: _ctx.$t('GenerateFromExternalData'),
    centered: "",
    onHide: onHide,
    onShown: reset,
    onClose: hideDialog,
    "no-close-on-backdrop": "",
    "no-close-on-esc": ""
  }, {
    "modal-footer": _withCtx(() => [
      (hasBackButton.value)
        ? (_openBlock(), _createBlock(_component_b_btn, {
            key: 0,
            class: "mr-2",
            disabled: backButtonDisabled.value,
            onClick: onBack
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(backButtonText.value), 1)
            ]),
            _: 1
          }, 8, ["disabled"]))
        : _createCommentVNode("", true),
      (hasNextButton.value)
        ? (_openBlock(), _createBlock(_component_b_btn, {
            key: 1,
            disabled: nextButtonDisabled.value,
            onClick: onNext
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(nextButtonText.value), 1)
            ]),
            _: 1
          }, 8, ["disabled"]))
        : _createCommentVNode("", true)
    ]),
    default: _withCtx(() => [
      (vm.currentStep === 'intro')
        ? (_openBlock(), _createBlock(intro, { key: 0 }))
        : _createCommentVNode("", true),
      (vm.currentStep === 'existingCatalog')
        ? (_openBlock(), _createBlock(existingCatalog, {
            key: 1,
            selectedExistingCatalogId: vm.selectedExistingCatalogId,
            "onUpdate:selectedExistingCatalogId": _cache[0] || (_cache[0] = ($event: any) => ((vm.selectedExistingCatalogId) = $event)),
            onSetCatalogName: _cache[1] || (_cache[1] = ($event: any) => (vm.selectedExistingCatalogName = $event))
          }, null, 8, ["selectedExistingCatalogId"]))
        : _createCommentVNode("", true),
      (vm.currentStep === 'catalogOptions')
        ? (_openBlock(), _createBlock(catalogOptions, {
            key: 2,
            onStepChange: setExternalDataSelection,
            onSelectedExternalData: setExternalDataSelection,
            fields: vm.fields,
            groupedCommands: vm.groupedCommands
          }, null, 8, ["fields", "groupedCommands"]))
        : _createCommentVNode("", true),
      (vm.currentStep === 'catalogNaming')
        ? (_openBlock(), _createBlock(catalogNaming, {
            key: 3,
            onStepChange: setCatalogName,
            onChange: onCatalogNameChange
          }))
        : _createCommentVNode("", true),
      (vm.currentStep === 'catalogFinishing')
        ? (_openBlock(), _createBlock(catalogFinishing, {
            key: 4,
            openGeneratedCatalogue: vm.openGeneratedCatalogue,
            "onUpdate:openGeneratedCatalogue": _cache[2] || (_cache[2] = ($event: any) => ((vm.openGeneratedCatalogue) = $event)),
            generatedCatalogData: vm.generatedCatalogData,
            errorPassed: vm.error,
            onFinish: onFinish
          }, null, 8, ["openGeneratedCatalogue", "generatedCatalogData", "errorPassed"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["modelValue", "title"]))
}
}

})